<template>
  <div class="team-requests">
    <div class="team-requests__head">
      <feather-icon
        icon="RadioIcon"
        size="14"
        class="mr-50"
      />
      <span>{{ $t('despacho.dispatchProgress.title') }}</span>
      <b-spinner
        v-if="loading"
        small
        class="ml-50"
      />
    </div>

    <div
      v-if="!loading && !requests.length"
      class="team-requests__empty"
    >
      {{ $t('despacho.requestSummary.empty') }}
    </div>

    <div
      v-else-if="!loading && !visible.length"
      class="team-requests__empty"
    >
      {{ $t('despacho.dispatchProgress.nothingPending') }}
    </div>

    <!-- Quanto falta enviar de cada tipo (só no modal de despacho). -->
    <div
      v-if="showProgress && !loading && (missingByType.length || allDone)"
      class="team-requests__progress"
      :class="allDone ? 'team-requests__progress--done' : 'team-requests__progress--missing'"
    >
      <template v-if="missingByType.length">
        <span class="team-requests__progress-title">
          <feather-icon
            icon="SendIcon"
            size="12"
            class="mr-25"
          />{{ $t('despacho.dispatchProgress.missingTitle') }}
        </span>
        <span
          v-for="m in missingByType"
          :key="m.id"
          class="team-requests__progress-chip"
        >{{ m.remaining }}× {{ m.name }}</span>
      </template>
      <span
        v-else
        class="team-requests__progress-title"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="13"
          class="mr-25"
        />{{ $t('despacho.dispatchProgress.allSent') }}
      </span>
    </div>

    <ul
      v-if="visible.length"
      class="team-requests__list"
    >
      <li
        v-for="req in visible"
        :key="req.id"
        class="team-requests__item"
        :class="'team-requests__item--' + statusKey(req.status)"
      >
        <span class="team-requests__dot" />

        <div class="team-requests__body">
          <div class="team-requests__title">
            {{ req.team_type ? req.team_type.name : $t('despacho.requestSummary.removedType') }}
            <span
              v-if="req.dispatch_code"
              class="team-requests__code"
            >{{ req.dispatch_code.code }}</span>
            <span class="team-requests__status">
              <feather-icon
                v-if="req.status === 2"
                icon="CheckIcon"
                size="12"
                class="mr-25"
              />{{ statusLabel(req) }}
            </span>
          </div>

          <div
            v-if="req.notes"
            class="team-requests__notes"
          >
            {{ req.notes }}
          </div>

          <div class="team-requests__meta">
            <feather-icon
              icon="UserIcon"
              size="10"
              class="mr-25"
            />
            {{ req.requested_by ? req.requested_by.name : '—' }}
            <span class="team-requests__sep">·</span>
            {{ req.created_at | trFormatDate }}
          </div>

        </div>

        <button
          v-if="canCancel"
          class="btn btn-sm btn-outline-danger team-requests__cancel"
          @click="$emit('cancel', req)"
        >
          {{ $t('common.cancel') }}
        </button>
      </li>
    </ul>

    <suggested-team-types
      :types="suggestedTypes"
      :fulfilled-ids="fulfilledTypeIds"
    />
  </div>
</template>

<script>
import SuggestedTeamTypes from './SuggestedTeamTypes.vue'

export default {
  name: 'OccurrenceTeamRequests',

  components: { SuggestedTeamTypes },

  filters: {
    trFormatDate(val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    },
  },

  props: {
    requests: { type: Array, default: () => [] },
    suggestedTypes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    canCancel: { type: Boolean, default: false },
    // Modal de despacho: mostra também o que já foi enviado e quanto falta de cada tipo.
    showProgress: { type: Boolean, default: false },
  },

  computed: {
    pending() {
      return this.requests.filter(r => r.status === 1)
    },

    // Com o progresso, as atendidas continuam na lista (marcadas); canceladas nunca aparecem aqui.
    visible() {
      return this.showProgress ? this.requests.filter(r => r.status !== 3) : this.pending
    },

    // O que ainda falta enviar, somado por tipo (uma solicitação pode ser de várias equipes).
    missingByType() {
      const byType = {}

      this.pending.forEach(r => {
        const remaining = r.remaining === undefined || r.remaining === null ? r.quantity : r.remaining
        if (!byType[r.team_type_id]) {
          byType[r.team_type_id] = { id: r.team_type_id, name: r.team_type ? r.team_type.name : '—', remaining: 0 }
        }
        byType[r.team_type_id].remaining += remaining
      })

      return Object.values(byType)
    },

    allDone() {
      return !this.pending.length && this.requests.some(r => r.status === 2)
    },

    // Tipos sugeridos já enviados por completo: há solicitação do tipo e todas estão atendidas.
    fulfilledTypeIds() {
      if (!this.showProgress) return []

      return this.suggestedTypes
        .filter(t => {
          const doTipo = this.requests.filter(r => Number(r.team_type_id) === Number(t.id) && r.status !== 3)

          return doTipo.length > 0 && doTipo.every(r => r.status === 2)
        })
        .map(t => t.id)
    },
  },

  methods: {
    statusKey(status) {
      return { 1: 'pending', 2: 'fulfilled', 3: 'cancelled' }[status] || 'pending'
    },

    statusLabel(req) {
      if (req.status === 2) return this.$t('despacho.dispatchProgress.sent')

      if (req.quantity > 1) {
        const remaining = req.remaining === undefined || req.remaining === null ? req.quantity : req.remaining

        return this.$t('despacho.dispatchProgress.remaining', { remaining, quantity: req.quantity })
      }

      return this.$t('despacho.dispatchProgress.pending')
    },
  },
}
</script>

<style scoped>
.team-requests { border:1px solid #ebe9f1; border-radius:8px; background:#fff; margin-bottom:16px; }
.team-requests__head {
  display:flex; align-items:center; padding:8px 12px; border-bottom:1px solid #f2f0f7;
  font-size:11px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:#6e6b7b;
}
.team-requests__empty { padding:14px 12px; font-size:12px; color:#b9b9c3; }
.team-requests__list { list-style:none; margin:0; padding:0; }
.team-requests__item { display:flex; align-items:flex-start; gap:8px; padding:10px 12px; border-bottom:1px solid #f7f6fb; }
.team-requests__item:last-child { border-bottom:0; }
.team-requests__dot { width:8px; height:8px; border-radius:50%; margin-top:5px; flex:0 0 8px; }
.team-requests__item--pending   .team-requests__dot { background:#ff9f43; }
.team-requests__item--fulfilled .team-requests__dot { background:#28c76f; }
.team-requests__item--cancelled .team-requests__dot { background:#b9b9c3; }
.team-requests__body { flex:1; min-width:0; }
.team-requests__title { font-size:13px; font-weight:600; color:#5e5873; }
.team-requests__item--cancelled .team-requests__title { text-decoration:line-through; color:#b9b9c3; }
.team-requests__code {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 9px;
  font-size: 10.5px;
  font-weight: 700;
  background: #eceffd;
  color: #4c5eb5;
}
.dark-layout .team-requests__code { background: #1f2547; color: #a3b0ff; }
.team-requests__status { font-size:11px; font-weight:600; margin-left:6px; }
.team-requests__item--pending   .team-requests__status { color:#ff9f43; }
.team-requests__item--fulfilled .team-requests__status { color:#28c76f; }
.team-requests__item--cancelled .team-requests__status { color:#b9b9c3; }
.team-requests__notes { font-size:12px; color:#6e6b7b; margin-top:2px; }
.team-requests__meta { font-size:11px; color:#b9b9c3; margin-top:3px; }
.team-requests__sep { margin:0 4px; }
.team-requests__cancel { flex:0 0 auto; }
.team-requests__progress {
  display:flex; flex-wrap:wrap; align-items:center; gap:6px 8px; padding:8px 12px;
  border-bottom:1px solid #f2f0f7; font-size:12px;
}
.team-requests__progress--missing { background:rgba(255,159,67,.12); color:#b45f06; }
.team-requests__progress--done { background:rgba(40,199,111,.14); color:#1f9d57; }
.team-requests__progress-title { display:inline-flex; align-items:center; font-weight:700; }
.team-requests__progress-chip {
  padding:1px 10px; border-radius:12px; border:1px solid currentColor; font-weight:700;
}

.dark-layout .team-requests { background:#283046; border-color:#3b4253; }
.dark-layout .team-requests__head { color:#d0d2d6; border-color:#3b4253; }
.dark-layout .team-requests__title { color:#d0d2d6; }
.dark-layout .team-requests__item { border-color:#3b4253; }
</style>
